import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import InternalSidebar from '../../components/InternalSidebar';
import InternalTopMenu from '../../components/InternalTopMenu';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';

const ChangePassword = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isFormValid = currentPassword && newPassword && confirmPassword;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('As novas senhas não coincidem.');
      return;
    }

    if (!user?.id) {
      setError('Não foi possível identificar o usuário autenticado.');
      return;
    }

    try {
      setSubmitting(true);
      await api.post('/security/changepassword', {
        userId: user.id,
        currentPassword,
        newPassword,
      });

      setSuccess('Senha alterada com sucesso.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Não foi possível alterar a senha.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  return (
    <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-white shadow-sm">
            <button
              type="button"
              className="text-2xl text-slate-700"
              onClick={toggleSidebar}
              aria-label="Abrir menu"
            >
              <i className="bi-list"></i>
            </button>
            <img src="/img/branded/logob.png" alt="Stackfy" className="h-8" />
            <div className="w-7" />
          </div>
        </div>

        <InternalSidebar active="account-section" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          ></button>
        )}

        <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12">
          <EmailVerificationBanner />
          <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <button
              type="button"
              onClick={() => navigate('/account')}
              className="text-sm font-semibold flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <i className="bi-arrow-left-short text-xl"></i> Voltar
            </button>
            <InternalTopMenu onLogout={logout} />
          </header>

          <section className="max-w-3xl mx-auto">
            <div className="rounded-3xl border border-slate-200 bg-white px-6 py-8 shadow-sm">
              <h1 className="text-xl font-semibold text-slate-900 mb-6">Alterar Senha</h1>

              {error && (
                <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
              )}

              {success && (
                <div className="mb-4 rounded border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  {success}
                </div>
              )}

              <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
                <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <i className="bi-lock text-slate-400 text-lg"></i>
                  <input
                    type={showCurrent ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Senha atual"
                    className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent((prev) => !prev)}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <i className={showCurrent ? 'bi-eye' : 'bi-eye-slash'}></i>
                  </button>
                </div>

                <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <i className="bi-lock text-slate-400 text-lg"></i>
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Nova senha"
                    className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew((prev) => !prev)}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <i className={showNew ? 'bi-eye' : 'bi-eye-slash'}></i>
                  </button>
                </div>

                <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <i className="bi-lock text-slate-400 text-lg"></i>
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repetir nova senha"
                    className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((prev) => !prev)}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <i className={showConfirm ? 'bi-eye' : 'bi-eye-slash'}></i>
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={!isFormValid || submitting}
                  className={`mt-4 inline-flex items-center justify-center rounded-full px-6 py-2 text-sm font-semibold transition-colors ${
                    !isFormValid || submitting
                      ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      : 'bg-tBlue text-white hover:bg-tBlue2'
                  }`}
                >
                  {submitting ? 'Alterando...' : 'Alterar'}
                </button>
              </form>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default ChangePassword;

